import { useEffect, useState } from "react";

import {
  faChevronDown,
  faExclamationTriangle,
  faKey,
  faPlus,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "src/base-components/Button";
import { DropDown } from "src/base-components/DropDown";
import { Tabs } from "src/base-components/Tabs";
import { Icon } from "src/design-system/Icon";
import type { CredentialEnvName } from "src/settings/credentials/model";

const ENVIRONMENT_DISPLAY_NAMES: Record<CredentialEnvName, string> = {
  live: "Production",
  sandbox: "Sandbox",
};

const AVAILABLE_ENVIRONMENTS: CredentialEnvName[] = ["live", "sandbox"];

type EmptyStateProps = {
  availableEnvironments: CredentialEnvName[];
  onAddEnvironment: (env: CredentialEnvName) => void;
};

const EmptyState: React.FC<EmptyStateProps> = ({
  availableEnvironments,
  onAddEnvironment,
}) => (
  <div className="flex w-full flex-col items-center justify-center rounded-lg bg-gray-50 px-4 pt-14 text-center">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100">
      <Icon icon={faKey} size="sm" variant="active" />
    </div>
    <span className="mb-2 font-inter-medium-13px text-gray-900">
      No API keys configured
    </span>
    <div className="mb-10">
      <DropDown
        buttonClassName="border font-inter-semibold-13px text-gray-800 !ring-0 rounded-lg pl-3 py-1 mb-1"
        dataLoc="credential-add-environment"
        elements={availableEnvironments.map((env) => ({
          key: env,
          value: env,
        }))}
        placement="bottomLeft"
        renderButtonValue={() => (
          <div
            className="flex items-center justify-between"
            data-loc="add-environment-to-credential"
          >
            <span className="border-r pr-2">Add API key</span>
            <div className="px-1">
              <Icon icon={faChevronDown} size="xs" />
            </div>
          </div>
        )}
        renderValue={({ value }) => (
          <div
            className="flex items-center justify-between whitespace-nowrap p-4 py-3"
            data-loc={`add-${value}-environment-to-credential`}
          >
            <span>
              Add {ENVIRONMENT_DISPLAY_NAMES[value as CredentialEnvName]} API
              key
            </span>
          </div>
        )}
        onSelect={(value) => onAddEnvironment(value as CredentialEnvName)}
      />
    </div>
  </div>
);

type EnvironmentDropdownProps = {
  availableEnvironments: CredentialEnvName[];
  onAddEnvironment: (env: CredentialEnvName) => void;
};

const EnvironmentDropDown: React.FC<EnvironmentDropdownProps> = ({
  availableEnvironments,
  onAddEnvironment,
}) => (
  <DropDown
    buttonClassName="border-0 font-inter-semibold-13px text-gray-800 !ring-0"
    elements={availableEnvironments.map((env) => ({ key: env, value: env }))}
    placement="bottomLeft"
    renderButtonValue={() => (
      <div
        className="flex items-center justify-between gap-1"
        data-loc="additional-credential-environment-button"
      >
        <Icon icon={faPlus} size="xs" />
        <span>Add API key</span>
      </div>
    )}
    renderValue={({ value }) => (
      <div
        className="flex items-center justify-between whitespace-nowrap p-4 py-3"
        data-loc={`add-${value}-environment-to-credential`}
      >
        <span>
          Add {ENVIRONMENT_DISPLAY_NAMES[value as CredentialEnvName]} API key
        </span>
      </div>
    )}
    variant="transparent"
    onSelect={(value) => onAddEnvironment(value as CredentialEnvName)}
  />
);

type Props = {
  addedEnvironments: CredentialEnvName[];
  renderEnvironmentFields: (env: CredentialEnvName) => React.ReactNode;
  hasEnvironmentError: (env: CredentialEnvName) => boolean;
  onAddEnvironment: (env: CredentialEnvName) => void;
  onRemoveEnvironment: (env: CredentialEnvName) => void;
};

export const CredentialEnvironmentsConfig: React.FC<Props> = ({
  addedEnvironments,
  renderEnvironmentFields,
  hasEnvironmentError,
  onAddEnvironment,
  onRemoveEnvironment,
}) => {
  const orderedAddedEnvironments = AVAILABLE_ENVIRONMENTS.filter((env) =>
    addedEnvironments.includes(env),
  );
  const missingEnvironments = AVAILABLE_ENVIRONMENTS.filter(
    (env) => !addedEnvironments.includes(env),
  );

  const [activeEnvironment, setActiveEnvironment] = useState<
    CredentialEnvName | undefined
  >(orderedAddedEnvironments[0]);

  useEffect(() => {
    if (
      activeEnvironment === undefined ||
      !orderedAddedEnvironments.includes(activeEnvironment)
    ) {
      setActiveEnvironment(orderedAddedEnvironments[0]);
    }
  }, [orderedAddedEnvironments, activeEnvironment]);

  // Activate the newly added env so the user lands on its empty fields
  // instead of the previously-active tab (mirrors ManifestEnvironmentsConfig).
  const handleAddEnvironment = (env: CredentialEnvName) => {
    onAddEnvironment(env);
    setActiveEnvironment(env);
  };

  // Move active to the remaining env synchronously: a useEffect-driven
  // switch would leave one render where selectedKey points at the just-
  // removed env and the Remove button still labels it.
  const handleRemoveActiveEnvironment = () => {
    if (activeEnvironment === undefined) return;
    const nextActive = orderedAddedEnvironments.find(
      (env) => env !== activeEnvironment,
    );
    setActiveEnvironment(nextActive);
    onRemoveEnvironment(activeEnvironment);
  };

  if (orderedAddedEnvironments.length === 0) {
    return (
      <div className="mb-6">
        <EmptyState
          availableEnvironments={AVAILABLE_ENVIRONMENTS}
          onAddEnvironment={handleAddEnvironment}
        />
      </div>
    );
  }

  return (
    <div className="mb-6">
      <Tabs
        containerClassName="flex flex-col"
        customButton={
          missingEnvironments.length > 0 ? (
            <EnvironmentDropDown
              availableEnvironments={missingEnvironments}
              onAddEnvironment={handleAddEnvironment}
            />
          ) : undefined
        }
        panelClassName="h-full flex flex-col rounded-lg bg-gray-50 px-4 pt-3 pb-6"
        panelsClassName="flex-1"
        selectedKey={activeEnvironment}
        tabClassName="px-0 mr-4 flex-shrink-0 cursor-pointer"
        tabListClassName="border-b border-gray-200 mb-4 sticky -top-5 bg-white z-10 flex"
        tabs={orderedAddedEnvironments.map((environment) => ({
          key: environment,
          label: (
            <div className="flex items-center gap-2">
              <span>{ENVIRONMENT_DISPLAY_NAMES[environment]} API key</span>
              {hasEnvironmentError(environment) && (
                <Icon variant="error" icon={faExclamationTriangle} size="sm" />
              )}
            </div>
          ),
          content: renderEnvironmentFields(environment),
        }))}
        unmountPanels={false}
        onChange={(key) => setActiveEnvironment(key as CredentialEnvName)}
      />
      {activeEnvironment !== undefined && (
        <div className="mt-4 flex w-fit">
          <Button
            dataLoc={`remove-${activeEnvironment}-credentials`}
            iconLeft={faTrashAlt}
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.currentTarget.blur();
              handleRemoveActiveEnvironment();
            }}
          >
            Remove {ENVIRONMENT_DISPLAY_NAMES[activeEnvironment]} API key
          </Button>
        </div>
      )}
    </div>
  );
};
